/**
 * Utility functions for rendering job status indicators and stage progress.
 * Extracted from UpgradeMedicPage to keep rendering logic reusable.
 */
import React from 'react';
import { Box, SpaceBetween, StatusIndicator } from '@amzn/awsui-components-core';
import { JobRun, JobStage } from '../types';
import { stripPipelinePrefix } from './wsMessageHandlers';

/** Render a Cloudscape StatusIndicator for a job status. */
export const renderJobStatus = (status: JobRun['status']): React.ReactNode => {
  switch (status) {
    case 'running':
      return <StatusIndicator type="in-progress">Running</StatusIndicator>;
    case 'connecting':
      return <StatusIndicator type="loading">Connecting</StatusIndicator>;
    case 'completed':
      return <StatusIndicator type="success">Completed</StatusIndicator>;
    case 'failed':
      return <StatusIndicator type="error">Failed</StatusIndicator>;
    case 'completed_with_alpha_deployment_failure':
      return <StatusIndicator type="warning">Completed (Alpha Deployment Failed)</StatusIndicator>;
    case 'cancelled':
      return <StatusIndicator type="stopped">Cancelled</StatusIndicator>;
    default:
      return <StatusIndicator type="stopped">{status}</StatusIndicator>;
  }
};

const STAGE_LABELS: Record<string, string> = {
  [JobStage.INITIALIZING]: 'Initializing',
  [JobStage.IMPACT_ANALYSIS]: 'Analyzing',
  [JobStage.CODE_CHANGES]: 'Coding',
  [JobStage.BUILD]: 'Build & Release',
  [JobStage.CR_CREATION]: 'CR Creation',
  [JobStage.PIPELINE_TRIGGER]: 'Deploying',
  [JobStage.CLEANUP]: 'Cleanup',
  [JobStage.COMPLETED]: 'Completed',
  [JobStage.FAILED]: 'Failed',
  // Backend alternate stage names
  DISCOVERY: 'Discovery',
  ANALYSIS: 'Analyzing',
  CODING: 'Coding',
  BUILDING: 'Building',
  DEPLOYMENT: 'Deploying',
  DEPLOYING: 'Deploying',
  RELEASING: 'Releasing',
  FINALIZATION: 'Finalizing',
  ALPHA_DEPLOYMENT: 'Deploying'
};

const BASE_STAGE_ORDER = [JobStage.INITIALIZING, JobStage.IMPACT_ANALYSIS, JobStage.CODE_CHANGES, JobStage.BUILD, JobStage.CLEANUP];
const EXTENDED_STAGE_ORDER = [
  JobStage.INITIALIZING,
  JobStage.IMPACT_ANALYSIS,
  JobStage.CODE_CHANGES,
  JobStage.BUILD,
  JobStage.PIPELINE_TRIGGER,
  JobStage.CLEANUP
];

/**
 * Map backend stage names to our JobStage enum.
 * The backend may send different names (e.g. DISCOVERY, DEPLOYMENT) than our enum values.
 */
const BACKEND_STAGE_MAP: Record<string, JobStage> = {
  // Exact enum matches
  [JobStage.INITIALIZING]: JobStage.INITIALIZING,
  [JobStage.IMPACT_ANALYSIS]: JobStage.IMPACT_ANALYSIS,
  [JobStage.CODE_CHANGES]: JobStage.CODE_CHANGES,
  [JobStage.BUILD]: JobStage.BUILD,
  [JobStage.CR_CREATION]: JobStage.CR_CREATION,
  [JobStage.PIPELINE_TRIGGER]: JobStage.PIPELINE_TRIGGER,
  [JobStage.CLEANUP]: JobStage.CLEANUP,
  [JobStage.COMPLETED]: JobStage.COMPLETED,
  [JobStage.FAILED]: JobStage.FAILED,
  [JobStage.CANCELLED]: JobStage.CANCELLED,
  // Backend alternate names
  DISCOVERY: JobStage.INITIALIZING,
  ANALYSIS: JobStage.IMPACT_ANALYSIS,
  CODING: JobStage.CODE_CHANGES,
  BUILDING: JobStage.BUILD,
  DEPLOYMENT: JobStage.PIPELINE_TRIGGER,
  DEPLOYING: JobStage.PIPELINE_TRIGGER,
  ALPHA_DEPLOYMENT: JobStage.PIPELINE_TRIGGER,
  RELEASING: JobStage.CLEANUP,
  FINALIZATION: JobStage.CLEANUP
};

/** Normalize a backend stage name to our JobStage enum value. */
const normalizeStage = (stage: string): JobStage | undefined => BACKEND_STAGE_MAP[stage];

/** Check if a job has a deploy stage by inspecting stages record and currentStage. */
const jobHasDeployStage = (job: JobRun): boolean => {
  if (!job.stages) return false;
  const stageKeys = Object.keys(job.stages);
  // Check enum value or backend alternate names
  return (
    stageKeys.includes(JobStage.PIPELINE_TRIGGER) ||
    stageKeys.includes('DEPLOYMENT') ||
    stageKeys.includes('DEPLOYING') ||
    stageKeys.includes('ALPHA_DEPLOYMENT') ||
    normalizeStage(job.currentStage || '') === JobStage.PIPELINE_TRIGGER
  );
};

/** Render stage progress bar with label for a job. */
export const renderStageProgress = (job: JobRun): React.ReactNode => {
  const hasDeployStage = jobHasDeployStage(job);
  const stageOrder = hasDeployStage ? EXTENDED_STAGE_ORDER : BASE_STAGE_ORDER;
  const totalStages = stageOrder.length;

  let currentStageIndex = 1;
  if (job.currentStage) {
    const mapped = normalizeStage(job.currentStage);
    const lookupStage = mapped || job.currentStage;

    if (lookupStage === JobStage.COMPLETED || lookupStage === JobStage.FAILED || lookupStage === JobStage.CANCELLED) {
      currentStageIndex = totalStages;
    } else {
      const idx = stageOrder.indexOf(lookupStage as JobStage);
      if (idx >= 0) {
        currentStageIndex = idx + 1;
      } else if (lookupStage === JobStage.CR_CREATION) {
        const buildIdx = stageOrder.indexOf(JobStage.BUILD);
        currentStageIndex = buildIdx >= 0 ? buildIdx + 1 : totalStages - 1;
      }
    }
  }

  const progressPercent = Math.round((currentStageIndex / totalStages) * 100);
  const currentLabel = job.currentStage ? STAGE_LABELS[job.currentStage] || job.currentStage : 'Pending';

  return (
    <SpaceBetween direction="vertical" size="xxxs">
      <Box fontSize="body-s">
        {currentLabel} ({currentStageIndex}/{totalStages})
      </Box>
      <div
        style={{
          width: '100%',
          height: '4px',
          backgroundColor: '#e5e7eb',
          borderRadius: '2px',
          overflow: 'hidden'
        }}
      >
        <div
          style={{
            width: `${progressPercent}%`,
            height: '100%',
            backgroundColor: job.status === 'failed' ? '#ef4444' : '#3b82f6',
            transition: 'width 0.3s ease'
          }}
        />
      </div>
    </SpaceBetween>
  );
};

/** Format a duration from start/end timestamps. */
export const formatDuration = (startedAt: Date, completedAt?: Date): string => {
  const startTime = startedAt.getTime();
  const endTime = completedAt ? completedAt.getTime() : Date.now();
  const durationMs = endTime - startTime;
  const seconds = Math.floor(durationMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
  return `${seconds}s`;
};

/** Format a date for compact display (MM-DD HH:MM). */
export const formatStartedAt = (d: Date): string => {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** Extract error message from a failed job, handling nested DynamoDB format. */
export const extractJobErrorMessage = (job: JobRun): string => {
  let errorMsg = '';
  if (job.error?.message) {
    const msg = job.error.message as any;
    if (typeof msg === 'object' && msg !== null && 'S' in msg) {
      errorMsg = msg.S;
    } else {
      errorMsg = String(job.error.message);
    }
  } else if (
    job.progressMessage &&
    (job.progressMessage.includes('❌') ||
      job.progressMessage.includes('✗') ||
      job.progressMessage.includes('Failed') ||
      job.progressMessage.includes('Error'))
  ) {
    errorMsg = job.progressMessage;
  }
  // Strip leading error icons to avoid duplication, then strip pipeline name prefix
  if (errorMsg) {
    errorMsg = errorMsg.replace(/^[❌✗]\s*/, '');
    errorMsg = stripPipelinePrefix(errorMsg, job.pipelineId);
  }
  return errorMsg;
};

/** Group lambdas by stackName for manual pipelines. */
export const groupLambdasByStack = (lambdas: { id: string; stackName?: string; [key: string]: any }[]): Array<[string, typeof lambdas]> => {
  const stackGroups = new Map<string, typeof lambdas>();
  lambdas.forEach((lambda) => {
    const stackName = lambda.stackName || 'Manually created (no stack)';
    if (!stackGroups.has(stackName)) {
      stackGroups.set(stackName, []);
    }
    stackGroups.get(stackName)!.push(lambda);
  });
  return Array.from(stackGroups.entries()).sort((a, b) => {
    if (a[0] === 'Manually created (no stack)') return 1;
    if (b[0] === 'Manually created (no stack)') return -1;
    return a[0].localeCompare(b[0]);
  });
};
